import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Switch,
  Button,
  IconButton,
  Box,
  Typography,
  Chip,
  Divider,
  Alert,
  Grid,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import type { FilterRule } from '@/types/farm-profile';
import FilterRuleEditor from './FilterRuleEditor';
import InfoTextPreview, { infoTextHelperText } from './InfoTextPreview';
import { HELP_TEXT_MAX_LENGTH } from '@shared/help-text';
import TagPicker from '@/components/tags/TagPicker';
import ValidityModeSelect from './ValidityModeSelect';

export interface FormField {
  id: string;
  type: 'text' | 'number' | 'date' | 'boolean' | 'select' | 'multiselect' | 'file' | 'email' | 'url' | 'phone' | 'textarea';
  label: string;
  placeholder?: string;
  description?: string;
  options?: { value: string; label: string }[];
  defaultValue?: any;
  required: boolean;
  // Layout and grouping
  layoutWidth: 3 | 4 | 6 | 12; // Grid columns (12 = full width, 6 = half, 4 = third, 3 = quarter)
  sectionId?: string; // Which section this field belongs to
  displayOrder?: number; // Controls position within section (lower = earlier)
  unit?: string; // Suffix for number fields (e.g. "ha", "kg", "st")
  optionStyle?: 'inline' | 'card'; // Display style for select/multiselect options
  inputPlacement?: 'beside' | 'below'; // Input field placement relative to label
  filterRule?: FilterRule | null; // Farm-profile filter; null = always shown
  allowUpload?: boolean; // Farmer may attach documents as well as type an answer
  uploadTags?: string[]; // Tags the attached document gets
  validityMode?: 'annual' | 'expires' | 'none' | null;
}

export interface FormSection {
  id: string;
  title: string;
  description?: string;
  defaultExpanded: boolean;
  order: number;
}

interface FormFieldEditorProps {
  field: FormField;
  onUpdate: (field: FormField) => void;
  onDelete: () => void;
  availableSections: FormSection[];
  isDragging?: boolean;
}

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'textarea', label: 'Textarea' },
  { value: 'number', label: 'Nummer' },
  { value: 'date', label: 'Datum' },
  { value: 'boolean', label: 'Ja/Nej (toggle)' },
  { value: 'select', label: 'Enkelval (radio)' },
  { value: 'multiselect', label: 'Flerval (checkbox)' },
  { value: 'email', label: 'E-post' },
  { value: 'url', label: 'URL' },
  { value: 'phone', label: 'Telefon' },
  // Never rendered in the control view (toRenderRuns drops FILE). Superseded by
  // the "Tillåt dokumentuppladdning" toggle below; kept as a disabled option so
  // existing file fields still display their type instead of blanking the Select.
  { value: 'file', label: 'Fil (stöds inte — använd dokumentuppladdning)', disabled: true },
];

const LAYOUT_OPTIONS = [
  { value: 12, label: 'Hel bredd', icon: '█████████████' },
  { value: 6, label: 'Halv bredd', icon: '██████  ██████' },
  { value: 4, label: 'Tredjedel', icon: '████  ████  ████' },
  { value: 3, label: 'Kvart', icon: '███  ███  ███  ███' },
];

const FormFieldEditor: React.FC<FormFieldEditorProps> = ({
  field,
  onUpdate,
  onDelete,
  availableSections,
  isDragging = false,
}) => {
  const [localField, setLocalField] = useState<FormField>(field);
  const [newOption, setNewOption] = useState('');
  // Sync with parent updates
  useEffect(() => {
    setLocalField(field);
  }, [field]);

  const handleFieldUpdate = (updates: Partial<FormField>) => {
    const updatedField = { ...localField, ...updates };
    setLocalField(updatedField);
    onUpdate(updatedField);
  };

  const addOption = () => {
    if (!newOption.trim()) return;
    
    const newOptions = [
      ...(localField.options || []),
      { value: newOption.toLowerCase().replace(/\s+/g, '_'), label: newOption.trim() }
    ];
    
    handleFieldUpdate({ options: newOptions });
    setNewOption('');
  };

  const removeOption = (indexToRemove: number) => {
    const newOptions = localField.options?.filter((_, index) => index !== indexToRemove) || [];
    handleFieldUpdate({ options: newOptions });
  };

  const needsOptions = localField.type === 'select' || localField.type === 'multiselect';

  return (
    <Card 
      sx={{ 
        mb: 2,
        opacity: isDragging ? 0.5 : 1,
        border: isDragging ? '2px dashed #ccc' : 'none'
      }}
    >
      <CardContent>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1
          }}>
          <Box sx={{
            flex: 1
          }}>
            <Grid container spacing={2}>
              {/* Field ID and Type */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Fält-ID"
                  value={localField.id}
                  onChange={(e) => handleFieldUpdate({ id: e.target.value })}
                  placeholder="t.ex. farm_name"
                  helperText="Unikt ID för fältet (används i API)"
                />
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Fälttyp</InputLabel>
                  <Select
                    value={localField.type}
                    label="Fälttyp"
                    onChange={(e) => {
                      const updates: Partial<FormField> = {
                        type: e.target.value as FormField['type']
                      };
                      if (needsOptions && localField.options) {
                        updates.options = localField.options;
                      }
                      handleFieldUpdate(updates);
                    }}
                  >
                    {FIELD_TYPES.map((type) => (
                      <MenuItem
                        key={type.value}
                        value={type.value}
                        disabled={'disabled' in type && type.disabled}
                      >
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Field Label and Placeholder */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Etikett"
                  value={localField.label}
                  onChange={(e) => handleFieldUpdate({ label: e.target.value })}
                  placeholder="t.ex. Gårdnamn"
                  required
                />
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Placeholder (valfritt)"
                  value={localField.placeholder || ''}
                  onChange={(e) => handleFieldUpdate({ placeholder: e.target.value })}
                  placeholder="t.ex. Skriv ditt gårdnamn här"
                />
              </Grid>

              {/* Unit (number fields only) */}
              {localField.type === 'number' && (
                <Grid size={{ xs: 12, sm: 3 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Enhet (valfritt)"
                    value={localField.unit || ''}
                    onChange={(e) => handleFieldUpdate({ unit: e.target.value })}
                    placeholder="t.ex. ha, kg, st"
                  />
                </Grid>
              )}

              {/* Description */}
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Beskrivning (valfritt)"
                  value={localField.description || ''}
                  onChange={(e) => handleFieldUpdate({ description: e.target.value })}
                  multiline
                  rows={4}
                  placeholder="Hjälptext som visas via infoikonen vid fältet"
                  helperText={infoTextHelperText(localField.description)}
                  slotProps={{ htmlInput: { maxLength: HELP_TEXT_MAX_LENGTH } }}
                />
                <InfoTextPreview text={localField.description || ''} />
              </Grid>

              {/* Section Assignment */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Sektion</InputLabel>
                  <Select
                    value={localField.sectionId || ''}
                    label="Sektion"
                    onChange={(e) => {
                      const val = e.target.value as string;
                      if (val) {
                        handleFieldUpdate({ sectionId: val });
                      } else {
                        // Clear sectionId by rebuilding the field without it
                        const { sectionId: _, ...rest } = localField;
                        setLocalField(rest as FormField);
                        onUpdate(rest as FormField);
                      }
                    }}
                  >
                    <MenuItem value="">
                      <Typography variant="body2" sx={{
                        color: "text.secondary"
                      }}>
                        Ingen sektion (direkt under formulär)
                      </Typography>
                    </MenuItem>
                    {availableSections
                      .sort((a, b) => a.order - b.order)
                      .map((section) => (
                        <MenuItem key={section.id} value={section.id}>
                          {section.title}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Layout Width */}
              <Grid size={{ xs: 12, sm: 3 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Blockbredd</InputLabel>
                  <Select
                    value={localField.layoutWidth}
                    label="Blockbredd"
                    onChange={(e) => handleFieldUpdate({ layoutWidth: e.target.value as FormField['layoutWidth'] })}
                  >
                    {LAYOUT_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1
                          }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: "monospace",
                              fontSize: "10px"
                            }}>
                            {option.icon}
                          </Typography>
                          <Typography variant="body2">
                            {option.label}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Input Placement */}
              <Grid size={{ xs: 12, sm: 3 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Fältplacering</InputLabel>
                  <Select
                    value={localField.inputPlacement || 'beside'}
                    label="Fältplacering"
                    onChange={(e) => handleFieldUpdate({ inputPlacement: e.target.value as 'beside' | 'below' })}
                  >
                    <MenuItem value="beside">Bredvid</MenuItem>
                    <MenuItem value="below">Nedanför</MenuItem>
                  </Select>
                </FormControl>
              </Grid>


              {/* Required Toggle */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={localField.required}
                      onChange={(e) => handleFieldUpdate({ required: e.target.checked })}
                    />
                  }
                  label="Obligatoriskt fält"
                />
              </Grid>

              {/* Document upload. The farmer may then answer with text, a document,
                  or both — either one marks the field as answered. */}
              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!localField.allowUpload}
                      onChange={(e) => {
                        if (e.target.checked) {
                          handleFieldUpdate({ allowUpload: true });
                          return;
                        }
                        // Drop the upload config with the toggle so a disabled
                        // field does not keep stale tags or a stale pre-selection.
                        const { uploadTags: _tags, validityMode: _mode, ...rest } = localField;
                        const next = { ...rest, allowUpload: false };
                        setLocalField(next);
                        onUpdate(next);
                      }}
                    />
                  }
                  label="Tillåt dokumentuppladdning"
                />
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    display: 'block',
                    mt: -0.5
                  }}>
                  Fältet räknas besvarat när lantbrukaren skrivit ett svar eller laddat upp ett dokument.
                </Typography>
                {localField.allowUpload && (
                  <Box sx={{ mt: 1 }}>
                    <TagPicker
                      value={localField.uploadTags ?? []}
                      onChange={(uploadTags) => handleFieldUpdate({ uploadTags })}
                      size="small"
                      label="Taggar"
                      error={(localField.uploadTags ?? []).length === 0}
                      helperText="Uppladdade dokument får exakt dessa taggar i lantbrukarens pärm."
                    />
                    <Box sx={{ mt: 2 }}>
                      <ValidityModeSelect
                        value={localField.validityMode ?? null}
                        onChange={(validityMode) => handleFieldUpdate({ validityMode })}
                        size="small"
                      />
                    </Box>
                  </Box>
                )}
              </Grid>

              {/* Farm-profile filter — the field's own rule is the whole story. */}
              <Grid size={{ xs: 12 }}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Filterregel (valfritt)
                </Typography>
                <FilterRuleEditor
                  value={localField.filterRule ?? null}
                  onChange={(next) => handleFieldUpdate({ filterRule: next })}
                />
              </Grid>

              {/* Options for select/multiselect */}
              {needsOptions && (
                <Grid size={{ xs: 12 }}>
                  <Divider sx={{ my: 1 }} />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1
                    }}>
                    <Typography variant="subtitle2">
                      Svarsalternativ
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <InputLabel>Visningsstil</InputLabel>
                      <Select
                        value={localField.optionStyle || 'inline'}
                        label="Visningsstil"
                        onChange={(e) => handleFieldUpdate({ optionStyle: e.target.value as 'inline' | 'card' })}
                      >
                        <MenuItem value="inline">Standard</MenuItem>
                        <MenuItem value="card">Kort</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      mb: 2
                    }}>
                    <TextField
                      size="small"
                      label="Nytt alternativ"
                      value={newOption}
                      onChange={(e) => setNewOption(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addOption();
                        }
                      }}
                      sx={{ flex: 1 }}
                    />
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={addOption}
                      disabled={!newOption.trim()}
                    >
                      Lägg till
                    </Button>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 1
                    }}>
                    {localField.options?.map((option, index) => (
                      <Chip
                        key={option.value}
                        label={option.label}
                        onDelete={() => removeOption(index)}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Box>

                  {!localField.options?.length && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      Lägg till minst ett alternativ för detta fält.
                    </Alert>
                  )}
                </Grid>
              )}
            </Grid>
          </Box>

          <IconButton
            color="error"
            onClick={onDelete}
            size="small"
            sx={{ mt: 1 }}
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      </CardContent>
    </Card>
  );
};

export default FormFieldEditor;